import { useQuery } from "@tanstack/react-query";
import algoliasearch, { SearchClient, SearchIndex } from "algoliasearch";
import {
  QUERY_CURRENT_PARTNER,
  getCurrentPartner
} from "api/landlord/landlord";
import config from "config/config";
import {
  useEffect,
  useState,
  createContext,
  FC,
  ReactNode,
  useContext
} from "react";

enum AlgoliaStatus {
  LOADING = "LOADING",
  SUCCESS = "SUCCESS",
  ERROR = "ERROR"
}

type Payload = { client: SearchClient; index: SearchIndex };

type AlgoliaContextT = {
  status: AlgoliaStatus;
  payload: Payload | null;
};

const AlgoliaContext = createContext<AlgoliaContextT>({
  payload: null,
  status: AlgoliaStatus.LOADING
});

export const AlgoliaProvider: FC<{ children?: ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<AlgoliaStatus>(AlgoliaStatus.LOADING);
  const [payload, setPayload] = useState<Payload | null>(null);

  const {
    data: currentPartner,
    isLoading,
    error
  } = useQuery({
    queryKey: [QUERY_CURRENT_PARTNER],
    queryFn: getCurrentPartner
  });

  useEffect(() => {
    if (isLoading) {
      setStatus(AlgoliaStatus.LOADING);
      setPayload(null);
    } else if (error) {
      setStatus(AlgoliaStatus.ERROR);
      setPayload(null);
    } else if (
      currentPartner?.partner.algolia?.client_search_key &&
      currentPartner.partner.algolia.index_name
    ) {
      const client = algoliasearch(
        currentPartner.partner.algolia.app_id || config.algoliaAppId!,
        currentPartner.partner.algolia.client_search_key
      );
      const index = client.initIndex(currentPartner.partner.algolia.index_name);

      setStatus(AlgoliaStatus.SUCCESS);
      setPayload({ client, index });
    } else {
      setStatus(AlgoliaStatus.ERROR);
      setPayload(null);
    }
  }, [isLoading, currentPartner, error]);

  return (
    <AlgoliaContext.Provider value={{ payload, status }}>
      {children}
    </AlgoliaContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export function useAlgoliaContext() {
  return useContext(AlgoliaContext);
}
